const DEFAULT_MATCH_PERIODS = new Set(['', 'match', 'reg_time', 'regular_time', 'full_time']);

const MAIN_KEYS_BY_SPORT = {
  soccer: {
    result: ['moneyline', 'moneyline_3-way', 'moneyline_3_way'],
    handicap: ['asian_handicap', 'goal_spread_3-way', 'goal_spread_3_way', 'goal_spread'],
    total: ['asian_total_goals', 'total_goals'],
  },
  baseball: {
    result: ['moneyline'],
    handicap: ['run_line'],
    total: ['total_runs'],
  },
  basketball: {
    result: ['moneyline'],
    handicap: ['point_spread'],
    total: ['total_points'],
  },
  volleyball: {
    result: ['moneyline'],
    handicap: ['set_handicap', 'point_spread'],
    total: ['total_points', 'total_sets'],
  },
  'ice-hockey': {
    result: ['moneyline_3-way', 'moneyline_3_way', 'moneyline'],
    handicap: ['puck_line', 'goal_spread', 'asian_handicap'],
    total: ['total_goals'],
  },
};

const UNSAFE_KEY_PATTERN = /(?:^|_)(?:player|corner|corners|card|cards|shot|shots|score|scorer|correct_score|exact|race|margin|method|round|map|tower|baron|dragon|roshan|objective|combo|double_chance|draw_no_bet|both_teams_to_score|team_total)(?:_|$)/;
const RESULT_KEY_PATTERN = /(^|_)moneyline(_|$)|(^|_)moneyline_3(-|_)way(_|$)/;

export const normalizeDomesticKey = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9.+-]+/g, '_')
  .replace(/^_+|_+$/g, '');

export const normalizeDomesticSport = (value) => {
  const slug = normalizeDomesticKey(value).replace(/_/g, '-');
  if (['hockey', 'ice-hockey', 'icehockey'].includes(slug)) return 'ice-hockey';
  return slug;
};

const keyOfMarket = (market) => normalizeDomesticKey(
  market?.marketTypeKey
    ?? market?.market_type_key
    ?? market?.provider_market_key
    ?? market?.providerMarketKey
    ?? market?.market_id
    ?? market?.marketId
);

const familyOfMarket = (market) => normalizeDomesticKey(
  market?.marketFamily
    ?? market?.market_family
    ?? market?.canonical_family
    ?? market?.marketCategoryKey
    ?? market?.market_category_key
);

const periodOfMarket = (market) => normalizeDomesticKey(market?.periodScope ?? market?.period_scope);

const textOfMarket = (market) => normalizeDomesticKey([
  keyOfMarket(market),
  market?.name,
  market?.displayNameKo,
  market?.display_name_ko,
  market?.displayName,
  market?.display_name,
  market?.provider_market_name,
].filter(Boolean).join(' '));

export const domesticMarketRole = (market) => {
  const key = keyOfMarket(market);
  const family = familyOfMarket(market);
  const text = textOfMarket(market);
  if (family === 'result' || RESULT_KEY_PATTERN.test(key)) return 'result';
  if (family === 'handicap' || family === 'spread') return 'handicap';
  if (family === 'total') return 'total';
  if (/(^|_)(handicap|spread|run_line|point_spread|puck_line|goal_spread)(_|$)/.test(text)) return 'handicap';
  if (/(^|_)(total|over_under|overunder)(_|$)/.test(text)) return 'total';
  return null;
};

export const isDomesticMarketEligible = (market, sportSlug) => {
  const sport = normalizeDomesticSport(sportSlug ?? market?.sport_slug ?? market?.sport ?? market?.sportSlug);
  if (!sport || sport === 'esports') return false;
  const role = domesticMarketRole(market);
  if (!['result', 'handicap', 'total'].includes(role)) return false;
  const text = textOfMarket(market);
  if (text.includes('+')) return false;
  if (!text || UNSAFE_KEY_PATTERN.test(text)) return false;
  if (role === 'result') return RESULT_KEY_PATTERN.test(text);
  if (role === 'handicap') return /(^|_)(handicap|spread|run_line|point_spread|puck_line|goal_spread)(_|$)/.test(text);
  return /(^|_)(total|over_under|overunder)(_|$)/.test(text);
};

export const isDomesticDefaultMarket = (market, sportSlug) => {
  if (!isDomesticMarketEligible(market, sportSlug)) return false;
  const sport = normalizeDomesticSport(sportSlug ?? market?.sport_slug ?? market?.sport ?? market?.sportSlug);
  const role = domesticMarketRole(market);
  const key = keyOfMarket(market);
  return DEFAULT_MATCH_PERIODS.has(periodOfMarket(market))
    && (MAIN_KEYS_BY_SPORT[sport]?.[role] ?? []).includes(key);
};

const priorityOfMarket = (market, sportSlug) => {
  const role = domesticMarketRole(market);
  const sport = normalizeDomesticSport(sportSlug ?? market?.sport_slug ?? market?.sport ?? market?.sportSlug);
  const key = keyOfMarket(market);
  const roleBase = role === 'result' ? 0 : role === 'handicap' ? 10 : role === 'total' ? 20 : 1000;
  const periodPenalty = DEFAULT_MATCH_PERIODS.has(periodOfMarket(market)) ? 0 : 100;
  const index = (MAIN_KEYS_BY_SPORT[sport]?.[role] ?? []).indexOf(key);
  return roleBase + periodPenalty + (index >= 0 ? index : 50);
};

const priceOfOdd = (odd) => Number(odd?.priceDecimal ?? odd?.price_decimal ?? odd?.odds ?? odd?.price);

const oddIsOpen = (odd) => Number.isFinite(priceOfOdd(odd))
  && priceOfOdd(odd) > 1
  && odd?.bettable !== false
  && odd?.isBettable !== false
  && odd?.is_bettable !== false;

const marketOdds = (market) => {
  const odds = market?.odds ?? market?.selections ?? [];
  return Array.isArray(odds) ? odds : [];
};

const selectionRoleFields = (odd) => [
  odd?.selectionRole,
  odd?.selection_role,
  odd?.normalizedSelection,
  odd?.normalized_selection,
  odd?.selectionLine,
  odd?.selection_line,
  odd?.teamSide,
  odd?.team_side,
  odd?.canonicalSelectionKey,
  odd?.canonical_selection_key,
  odd?.selectionKey,
  odd?.selection_key,
  odd?.displayName,
  odd?.name,
  odd?.selection,
].filter(Boolean);

const rolesFromField = (value, expectedRoles) => {
  const normalized = normalizeDomesticKey(value);
  const text = String(value ?? '').toLowerCase();
  return expectedRoles.filter((role) => {
    if (new RegExp(`(^|_)${role}(_|$)`).test(normalized)) return true;
    if (role === 'over') return text.includes('오버');
    if (role === 'under') return text.includes('언더');
    return false;
  });
};

export const domesticOddRole = (odd, marketRole) => {
  const expectedRoles = marketRole === 'total'
    ? ['over', 'under']
    : marketRole === 'handicap'
      ? ['home', 'away']
      : [];
  if (!expectedRoles.length) return null;

  const roles = new Set();
  for (const field of selectionRoleFields(odd)) {
    const candidates = rolesFromField(field, expectedRoles);
    if (candidates.length > 1) return null;
    if (candidates.length === 1) roles.add(candidates[0]);
  }
  return roles.size === 1 ? [...roles][0] : null;
};

const firstLineValue = (...values) => values.find((value) => value !== null && value !== undefined && value !== '');

const normalizedLineIdentity = (value, marketRole) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const line = marketRole === 'handicap' ? Math.abs(numeric) : numeric;
  return String(line);
};

const twoWayLineIdentity = (odd, market, marketRole) => {
  const pairKey = firstLineValue(
    odd?.canonicalPairKey,
    odd?.canonical_pair_key,
    market?.canonicalPairKey,
    market?.canonical_pair_key,
  );
  if (pairKey != null) return `pair:${normalizeDomesticKey(pairKey)}`;

  const line = firstLineValue(
    odd?.canonicalLineKey,
    odd?.canonical_line_key,
    odd?.line,
    odd?.points,
    odd?.selectionLine,
    odd?.selection_line,
    market?.canonicalLineKey,
    market?.canonical_line_key,
    market?.line,
    market?.lineKey,
  );
  const normalized = normalizedLineIdentity(line, marketRole);
  return normalized == null ? null : `line:${normalized}`;
};

export const resolveDomesticTwoWayOdds = (market, marketRole) => {
  const [leftRole, rightRole] = marketRole === 'total'
    ? ['over', 'under']
    : marketRole === 'handicap'
      ? ['home', 'away']
      : [];
  if (!leftRole || !rightRole) return { left: null, right: null };

  const candidates = marketOdds(market).reduce((acc, odd) => {
    const role = domesticOddRole(odd, marketRole);
    if (role === leftRole) acc.left.push(odd);
    if (role === rightRole) acc.right.push(odd);
    return acc;
  }, { left: [], right: [] });
  const left = candidates.left.length === 1 ? candidates.left[0] : null;
  const right = candidates.right.length === 1 ? candidates.right[0] : null;
  const leftId = left?.oddStateId ?? left?.id ?? left?.odd_state_id ?? null;
  const rightId = right?.oddStateId ?? right?.id ?? right?.odd_state_id ?? null;

  if (left && right) {
    const leftLine = twoWayLineIdentity(left, market, marketRole);
    const rightLine = twoWayLineIdentity(right, market, marketRole);
    if (!leftLine || !rightLine || leftLine !== rightLine) return { left: null, right: null };
  }

  return left && right && leftId != null && String(leftId) === String(rightId)
    ? { left: null, right: null }
    : { left, right };
};

const marketIsOpen = (market) => {
  if (market?.suspended === true || market?.isSuspended === true || market?.is_suspended === true) return false;
  if (market?.bettable === false || market?.isBettable === false || market?.is_bettable === false) return false;
  return marketOdds(market).filter(oddIsOpen).length >= 2;
};

const marketHasMain = (market) => marketOdds(market)
  .some((odd) => odd?.main === true || odd?.is_main === true || odd?.isMain === true);

const timestampMs = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const marketAgeMs = (market) => {
  const timestamps = [
    market?.sourceReceivedAt,
    market?.source_received_at,
    market?.updatedAt,
    market?.updated_at,
    ...marketOdds(market).flatMap((odd) => [
      odd?.sourceReceivedAt,
      odd?.source_received_at,
      odd?.updatedAt,
      odd?.updated_at,
    ]),
  ].map(timestampMs).filter(Number.isFinite);
  if (!timestamps.length) return 0;
  return Math.max(0, Date.now() - Math.max(...timestamps));
};

const marketGap = (market) => {
  const prices = marketOdds(market).map(priceOfOdd).filter(Number.isFinite);
  if (prices.length !== 2) return Number.MAX_SAFE_INTEGER;
  return Math.abs(prices[0] - prices[1]);
};

const lineAbs = (market) => {
  const value = Number(market?.line ?? market?.lineKey ?? market?.canonicalLineKey);
  return Number.isFinite(value) ? Math.abs(value) : Number.MAX_SAFE_INTEGER;
};

const mainRank = (market, hasMainCandidate, mainClosedStale) => {
  if (!hasMainCandidate) return 0;
  const isMain = marketHasMain(market);
  const isOpen = marketIsOpen(market);
  if (isMain && isOpen) return 0;
  if (mainClosedStale && !isMain && isOpen) return 1;
  if (isMain) return mainClosedStale ? 2 : 1;
  return 3;
};

export const selectRepresentativeDomesticMarket = (markets = [], sportSlug) => {
  const candidates = markets.filter((market) => isDomesticMarketEligible(market, sportSlug));
  if (!candidates.length) return null;
  const mainCandidates = candidates.filter(marketHasMain);
  const hasMainCandidate = mainCandidates.length > 0;
  const mainClosedStale = hasMainCandidate
    && mainCandidates.every((market) => !marketIsOpen(market) && marketAgeMs(market) >= 30000);
  return [...candidates].sort((left, right) => {
    const selectedDiff = (right.representativeLineSelected === true ? 1 : 0) - (left.representativeLineSelected === true ? 1 : 0);
    if (selectedDiff !== 0) return selectedDiff;
    const mainDiff = mainRank(left, hasMainCandidate, mainClosedStale) - mainRank(right, hasMainCandidate, mainClosedStale);
    if (mainDiff !== 0) return mainDiff;
    const priorityDiff = priorityOfMarket(left, sportSlug) - priorityOfMarket(right, sportSlug);
    if (priorityDiff !== 0) return priorityDiff;
    const gapDiff = marketGap(left) - marketGap(right);
    if (gapDiff !== 0) return gapDiff;
    const lineDiff = lineAbs(left) - lineAbs(right);
    if (lineDiff !== 0) return lineDiff;
    return String(left.sortKey ?? left.marketGroupId ?? '').localeCompare(String(right.sortKey ?? right.marketGroupId ?? ''));
  })[0];
};

export const selectDomesticDefaultMarkets = (markets = [], sportSlug) => {
  const buckets = { handicap: [], total: [] };
  for (const market of markets) {
    if (!isDomesticDefaultMarket(market, sportSlug)) continue;
    const role = domesticMarketRole(market);
    if (buckets[role]) buckets[role].push(market);
  }
  return {
    handicap: selectRepresentativeDomesticMarket(buckets.handicap, sportSlug),
    total: selectRepresentativeDomesticMarket(buckets.total, sportSlug),
  };
};

export const selectDomesticAdditionalMarkets = (markets = [], sportSlug) => {
  const grouped = new Map();
  for (const market of markets) {
    if (!isDomesticMarketEligible(market, sportSlug)) continue;
    if (isDomesticDefaultMarket(market, sportSlug)) continue;
    const role = domesticMarketRole(market);
    const key = market?.baseMarketGroupKey
      ?? market?.displaySectionKey
      ?? `${role}:${periodOfMarket(market) || 'match'}:${keyOfMarket(market)}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(market);
  }
  return [...grouped.values()]
    .map((group) => selectRepresentativeDomesticMarket(group, sportSlug))
    .filter(Boolean)
    .sort((left, right) => priorityOfMarket(left, sportSlug) - priorityOfMarket(right, sportSlug));
};

export const oddsForDomesticMarket = marketOdds;
